import React, { useCallback } from 'react';
import { Alert, AlertDescription } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import {
  MembersTable,
  useClinicianManager,
  type OrgMemberResponse,
} from '../components/clinician-manager';
import { inviteUser, resendInvite } from '../services/api';
import {
  buildRogerDocLinksByEmail,
  createRogerDocAccountLink,
  listRogerDocAccountLinks,
  type RogerDocClinicianAccount,
} from '../services/accountLinkApi';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { MemberSettingsDialog } from '../components/rbac/role-assignment-dialog/MemberSettingsDialog';
import InviteUserToAgencyDialog, {
  type InviteRogerDocAccountDraft,
} from './RBAC/components/InviteUserToAgencyDialog';

// ─── Component ──────────────────────────────────────────────────────────────

const ClinicianManager: React.FC = () => {
  const cm = useClinicianManager();
  const { user, showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  const canInvite = hasPermission('clinician-mgr:invite-users');
  // The RogerDoc account-link API is gated on clinician-mgr:access, which is
  // only one of the two permissions that grant access to this page.
  const canCreateRogerDocAccounts = hasPermission('clinician-mgr:access');

  // Runs after the invite so accounts are only provisioned for users the invite
  // actually created. RogerDoc emails each new account a link to set their
  // password; a failure here leaves the invitation itself intact.
  const createRogerDocAccounts = useCallback(
    async (
      drafts: InviteRogerDocAccountDraft[],
      results: { email: string; success: boolean }[]
    ) => {
      const invitedEmails = new Set(
        results.filter(r => r.success).map(r => r.email.toLowerCase())
      );
      const invited = drafts.filter(d =>
        invitedEmails.has(d.email.toLowerCase())
      );

      if (invited.length === 0) return;

      // Resending an invite to a still-pending user reports success, so an
      // address that is already linked would otherwise reach the create
      // endpoint a second time and produce a duplicate RogerDoc account.
      // Read the links fresh instead of trusting the list behind the table,
      // which can be in flight, failed, or stale: the create endpoint is not
      // idempotent, so a wrong answer here costs a duplicate account. If the
      // check cannot be made, provision nothing rather than risk duplicates.
      const agencyId = user?.agency?.id;
      let freshLinks: Record<string, RogerDocClinicianAccount[]> | null = null;
      if (agencyId) {
        try {
          freshLinks = buildRogerDocLinksByEmail(
            await listRogerDocAccountLinks(),
            agencyId
          );
        } catch (err) {
          console.error('Failed to load RogerDoc account links:', err);
        }
      }
      if (!freshLinks) {
        showError(
          'Invitation sent, but we could not check for existing RogerDoc accounts, so none were created. You can create them from the member settings.'
        );
        return;
      }
      const linksByEmail = freshLinks;

      const isAlreadyLinked = (email: string) =>
        (linksByEmail[email.toLowerCase()] ?? []).length > 0;
      const alreadyLinked = invited.filter(d => isAlreadyLinked(d.email));
      const pending = invited.filter(d => !isAlreadyLinked(d.email));

      if (alreadyLinked.length > 0) {
        showSuccess(
          alreadyLinked.length === 1
            ? `${alreadyLinked[0].email} already has a linked RogerDoc account`
            : `${alreadyLinked.length} invitees already have a linked RogerDoc account`,
          'No new account was created for them.'
        );
      }

      if (pending.length === 0) return;

      const outcomes = await Promise.all(
        pending.map(async draft => {
          try {
            await createRogerDocAccountLink({
              user_email: draft.email,
              first_name: draft.first_name,
              last_name: draft.last_name,
              clinician_title: draft.clinician_title,
              username: draft.username,
            });
            return { email: draft.email, error: null as string | null };
          } catch (err) {
            console.error('Failed to create RogerDoc account:', err);
            // Surface an actionable 4xx (e.g. the duplicate-name conflict);
            // fall back to a generic message for 5xx / network errors.
            return {
              email: draft.email,
              error: getBusinessErrorMessage(err) ?? 'Please try again.',
            };
          }
        })
      );

      const failures = outcomes.filter(outcome => outcome.error !== null);
      const createdCount = outcomes.length - failures.length;
      if (createdCount > 0) {
        showSuccess(
          createdCount === 1
            ? 'RogerDoc account created and linked'
            : `${createdCount} RogerDoc accounts created and linked`
        );
      }
      failures.forEach(failure => {
        showError(
          `Invitation sent, but the RogerDoc account for ${failure.email} was not created. ${failure.error}`
        );
      });

      await cm.loadRogerDocLinks();
    },
    [showSuccess, showError, user?.agency?.id, cm.loadRogerDocLinks]
  );

  const handleInvite = useCallback(
    async (params: {
      emails: string[];
      agency_id: string;
      role_ids: string[];
      roger_doc_accounts: InviteRogerDocAccountDraft[];
    }) => {
      try {
        const res = await inviteUser({
          body: {
            emails: params.emails,
            role_ids: params.role_ids,
            // The name and title collected for a RogerDoc account also seed the
            // invitee's RogerEMR profile, so they are not typed twice.
            profiles: params.roger_doc_accounts.map(draft => ({
              email: draft.email,
              first_name: draft.first_name,
              last_name: draft.last_name,
              user_type: draft.user_type,
            })),
          },
        });
        const data = res.data;
        if (!data) throw new Error('No response data');

        if (data.failed_count === 0) {
          const count = data.invited_count;
          showSuccess(
            count === 1
              ? `Invitation sent to ${params.emails[0]}`
              : `Invitations sent to ${count} people`
          );
        } else if (data.invited_count > 0) {
          showSuccess(
            `${data.invited_count} of ${params.emails.length} invitations sent. ${data.failed_count} failed — please try again for the remaining addresses.`
          );
        } else {
          showError('Failed to send invitations. Please try again.');
          return;
        }

        await createRogerDocAccounts(params.roger_doc_accounts, data.results);

        cm.closeInviteDialog();
        cm.loadData();
      } catch (err) {
        console.error('Failed to invite users:', err);
        showError('Failed to send invitations. Please try again.');
      }
    },
    [
      showSuccess,
      showError,
      createRogerDocAccounts,
      cm.closeInviteDialog,
      cm.loadData,
    ]
  );

  const handleResendInvite = useCallback(
    async (member: OrgMemberResponse) => {
      try {
        // The API client resolves (rather than throws) on HTTP errors, so an
        // absent `data` is how we detect a failed resend. See handleInvite.
        const res = await resendInvite({
          body: { email: member.email },
        });
        if (!res.data) throw new Error('No response data');
        showSuccess(`Invitation resent to ${member.email}`);
      } catch (err) {
        console.error('Failed to resend invitation:', err);
        showError('Failed to resend invitation. Please try again.');
      }
    },
    [showSuccess, showError]
  );

  // ── Permission gate ──

  if (!cm.hasAccess) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access this page. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className='space-y-6'>
      {/* Members table */}
      <MembersTable
        members={cm.filteredMembers}
        isLoading={cm.isLoading}
        hasSearchQuery={!!cm.searchQuery}
        searchQuery={cm.searchQuery}
        onSearchChange={cm.setSearchQuery}
        onRefresh={cm.loadData}
        onEditMember={cm.openEditDialog}
        onInvite={cm.openInviteDialog}
        onResendInvite={handleResendInvite}
        canInvite={canInvite}
        rogerDocLinksByEmail={cm.rogerDocLinksByEmail}
        hiddenClinicIds={cm.kinnserClinicIds}
      />

      {/* Member settings dialog (name editing + role assignment) */}
      <MemberSettingsDialog
        clinics={cm.roleAssignableClinics}
        preserveRoleAssignmentsForClinicIds={cm.kinnserClinicIds}
        member={cm.editingMember}
        onClose={cm.closeEditDialog}
        onSuccess={() => {
          cm.closeEditDialog();
          cm.loadData();
        }}
        onRogerDocLinkChanged={cm.loadRogerDocLinks}
      />

      {/* Invite dialog */}
      <InviteUserToAgencyDialog
        open={cm.showInviteDialog}
        agencyId={user?.agency?.id ?? null}
        allowRogerDocAccounts={canCreateRogerDocAccounts}
        onClose={cm.closeInviteDialog}
        onInvite={handleInvite}
      />
    </div>
  );
};

export default ClinicianManager;
